import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { HelpCircle, Calculator } from 'lucide-react';

interface FinancialSummaryTableProps {
  confirmedTotal: number;
  invoicedTotal?: number;
  unbilledTotal?: number;
  paidTotal?: number;
  unpaidTotal?: number;
  // Phase 7.5.6: Proposed totals for templates and proposals
  proposedTotal?: number;
  proposedRequiredTotal?: number;
  isTemplate?: boolean;
  isProposal?: boolean;
}

/**
 * Phase 7 Batch 6 Task 5: FinancialSummaryTable
 *
 * Compact table displaying financial metrics with hover tooltips.
 * Replaces 5 horizontal cards for better space efficiency.
 */
export function FinancialSummaryTable({
  confirmedTotal,
  invoicedTotal,
  unbilledTotal,
  paidTotal,
  unpaidTotal,
  proposedTotal,
  proposedRequiredTotal,
  isTemplate,
  isProposal
}: FinancialSummaryTableProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Phase 7.5.6: Show proposed totals for templates/proposals, confirmed for active projects
  const showProposedTotals = isTemplate || isProposal;

  // Filter out undefined metrics (for templates, only show proposed totals)
  const metrics = [
    // For templates/proposals: show proposed totals
    showProposedTotals && proposedTotal !== undefined && {
      label: 'Proposed',
      value: proposedTotal,
      tooltip: 'Sum of all proposed billable items, including both required and optional',
      testId: 'proposed-total'
    },
    showProposedTotals && proposedRequiredTotal !== undefined && {
      label: 'Required',
      value: proposedRequiredTotal,
      tooltip: 'Proposed items that are required (excludes optional add-ons)',
      testId: 'proposed-required-total'
    },
    // For active projects: show confirmed total
    !showProposedTotals && {
      label: 'Confirmed',
      value: confirmedTotal,
      tooltip: 'Total value of accepted billable items',
      testId: 'confirmed-total'
    },
    invoicedTotal !== undefined && {
      label: 'Invoiced',
      value: invoicedTotal,
      tooltip: 'Amount that has been billed to the client',
      testId: 'invoiced-total'
    },
    unbilledTotal !== undefined && {
      label: 'Unbilled',
      value: unbilledTotal,
      tooltip: 'Confirmed work not yet invoiced (Confirmed minus Invoiced)',
      testId: 'unbilled-total'
    },
    paidTotal !== undefined && {
      label: 'Paid',
      value: paidTotal,
      tooltip: 'Payments received from the client',
      testId: 'paid-total'
    },
    unpaidTotal !== undefined && {
      label: 'Unpaid',
      value: unpaidTotal,
      tooltip: 'Outstanding invoices awaiting payment (Invoiced minus Paid)',
      testId: 'unpaid-total'
    }
  ].filter((metric): metric is { label: string; value: number; tooltip: string; testId: string } => Boolean(metric));

  // Build summary stats to show when collapsed
  const summaryStats = showProposedTotals
    ? `Proposed: ${formatCurrency(proposedTotal ?? 0)}`
    : unbilledTotal !== undefined
      ? `Confirmed: ${formatCurrency(confirmedTotal)} · Unbilled: ${formatCurrency(unbilledTotal)}`
      : `Confirmed: ${formatCurrency(confirmedTotal)}`;

  return (
    <TooltipProvider>
      <div className="mt-6" data-testid="financial-summary-table">
        <CollapsibleSection
          title="Financial Summary"
          icon={<Calculator className="w-4 h-4 text-gray-500" />}
          defaultOpen={false}
          summary={summaryStats}
          testId="section-financial-summary"
          className="border-gray-100"
        >
          <div className="space-y-2">
            {metrics.map((metric) => (
              <div
                key={metric.label}
                className="flex items-center justify-between py-2 border-b last:border-b-0"
                data-testid={metric.testId}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-muted-foreground">
                    {metric.label}:
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle
                        className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help"
                        aria-label={metric.tooltip}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{metric.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-sm font-semibold" data-testid={`${metric.testId}-value`}>
                  {formatCurrency(metric.value)}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    </TooltipProvider>
  );
}
